import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'
import type { HuntDog } from '../lib/types'
import { Icon } from './Icon'
import { PlanDogAvatar } from './PlanDogAvatar'

export interface HuntDogsSectionProps {
  /** The dogs already registered on the plan. */
  dogs: HuntDog[]
  /** Open the add/edit picker. */
  onAddEdit: () => void
  /**
   * Unregister a dog. The remove control only appears on a row where
   * {@link HuntDogsSectionProps.canRemove} returns true, so a host that never
   * allows removal can leave this out.
   */
  onRemove?: (dogId: string) => void
  /**
   * Whether the viewer may take this dog off the plan — their own dog always,
   * any dog when they lead the plan. Affordance only; the Cloud Function is the
   * real gate. Defaults to never (no remove control) when omitted.
   */
  canRemove?: (dog: HuntDog) => boolean
  /** A register/unregister is in flight — the action buttons disable. */
  busy?: boolean
}

/**
 * The planned-hunt "Dogs" section (CLUB-883): a title with the registered-dog
 * count, an Add/Edit pill, and either the empty-state copy or a row per
 * registered dog (avatar + name + owner). Follows the mobile section's
 * behaviour; the clubhouse container owns the data, the picker and the writes.
 * Presentation only — no data fetching, no domain imports.
 */
export function HuntDogsSection({ dogs, onAddEdit, onRemove, canRemove, busy = false }: HuntDogsSectionProps) {
  const { t } = useTranslation('huntInvitation')
  const count = dogs.length
  const hasDogs = count > 0

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Icon name="dog" size={14} />
        <div style={{ fontSize: 13, fontWeight: 700 }}>{t('dogs.section')}</div>
        {hasDogs && (
          <div style={{ fontSize: 11, color: COLORS.outline }}>
            {t('dogs.countAdded', { count })}
          </div>
        )}
        <button
          className="wh-btn wh-btn-outline wh-btn-sm"
          style={{ marginLeft: 'auto' }}
          onClick={onAddEdit}
          disabled={busy}
        >
          {hasDogs ? t('dogs.editButton') : t('dogs.addButton')}
        </button>
      </div>

      {!hasDogs ? (
        <div
          style={{
            fontSize: 13,
            color: COLORS.outline,
            padding: '12px 14px',
            background: COLORS.surfaceContainerLowest,
            border: `1px solid ${COLORS.surfaceContainerHighest}`,
            borderRadius: 8,
          }}
        >
          {t('dogs.empty')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {dogs.map((dog) => {
            const removable = !!onRemove && !!canRemove && canRemove(dog)
            return (
              <div
                key={dog.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  background: COLORS.surfaceContainerLowest,
                  border: `1px solid ${COLORS.surfaceContainerHighest}`,
                  borderRadius: 8,
                }}
              >
                <PlanDogAvatar colorArgb={dog.colorArgb} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {dog.name}
                  </div>
                  {dog.ownerName && (
                    <div
                      style={{
                        fontSize: 11,
                        color: COLORS.outline,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {dog.ownerName}
                    </div>
                  )}
                </div>
                {removable && (
                  <button
                    className="wh-btn-icon"
                    title={t('dogs.remove')}
                    aria-label={t('dogs.remove')}
                    onClick={() => onRemove?.(dog.id)}
                    disabled={busy}
                    style={{ color: COLORS.error }}
                  >
                    <Icon name="trash" size={16} />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
